import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Button from "../button/Button";
import clsx from "clsx";
import { useSecurity } from "../../api/settings/security";
import { Loader } from "../loader/Loader";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "../../layout/AppLayout";
import { PasswordInput } from "../input/passwordInput";
import { Arrow } from "../svg/Wallet/Wallet";
import { Card } from "../Card";

const rules = [
  { key: "lowercase", label: "One lowercase character" },
  { key: "uppercase", label: "One uppercase character" },
  { key: "number", label: "One number" },
  { key: "special", label: "One special  character" },
  { key: "length", label: "Min 8 characters" },
];

function ChangePassword() {
  const navigate = useNavigate();
  const [passwordsMatch, setPasswordsMatch] = useState(false);

  const [valid, setValid] = useState({
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
    length: false,
  });

  const validatePassword = (value) => {
    setPassword(value);
    const newValid = {
      lowercase: /[a-z]/.test(value),
      uppercase: /[A-Z]/.test(value),
      number: /[0-9]/.test(value),
      special: /[_!@#$%^&*(),.?":{}|<>]/.test(value),
      length: value.length >= 8,
    };
    setValid(newValid);
  };

  const {
    handleChangePassword,
    oldPassword,
    setOldPassword,
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    loading,
  } = useSecurity();

  useEffect(() => {
    if (password && passwordConfirm) {
      setPasswordsMatch(password === passwordConfirm);
    } else {
      setPasswordsMatch(false);
    }
  }, [password, passwordConfirm]);

  const allRulesPassed = Object.values(valid).every((rule) => rule === true);
  const isFormValid =
    oldPassword.trim() !== "" &&
    password.trim() !== "" &&
    passwordConfirm.trim() !== "" &&
    allRulesPassed &&
    passwordsMatch;

  return (
    <AppLayout noNav bg={"!bg-[#F8F8F8]"}>
      <div className="pt-10 pb-7 px-5 min-h-screen flex flex-col">
        <motion.div
          initial={{ opacity: 0, scale: 0.2 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.6,
            ease: [0.25, 0.46, 0.45, 0.94],
          }}
          className="w-full relative flex flex-col flex-grow"
        >
          <div className="relative flex items-center justify-center mb-10">
            <Arrow
              onClick={() => navigate(-1)}
              stroke="#fff"
              className="text-lg absolute left-0 cursor-pointer"
            />
            <p className="font-bold text-xl text-black"> Change Password</p>
          </div>

          <Card className="flex flex-col gap-5">
            <PasswordInput
              label="Old Password"
              placeholder="Old Password"
              value={oldPassword}
              onChange={(e) => {
                setOldPassword(e.target.value);
              }}
            />
            <PasswordInput
              label="New password"
              placeholder="New password"
              value={password}
              onChange={(e) => {
                validatePassword(e.target.value);
              }}
            />
            <PasswordInput
              label="Confirm password"
              placeholder="Confirm password"
              value={passwordConfirm}
              onChange={(e) => {
                setPasswordConfirm(e.target.value);
              }}
            />
            <div>
              <div className="grid grid-cols-6 gap-x-2 gap-y-0 mt-4 text-sm">
                {rules.map(({ key, label }) => (
                  <div
                    key={key}
                    className={clsx(
                      "flex items-center gap-2 leading-tight col-span-6 400:col-span-3",
                      key === "length" && "col-span-2"
                    )}
                  >
                    <span
                      className={clsx(
                        "h-1 w-1 rounded-full",
                        valid[key] ? "bg-green-600" : "bg-gray-300"
                      )}
                    />
                    <span
                      className={clsx(
                        valid[key] ? "text-black" : "text-gray-400",
                        "text-xs"
                      )}
                    >
                      {label}
                    </span>
                  </div>
                ))}
              </div>
              {passwordConfirm && (
                <div className="flex items-center gap-2 text-sm mt-3">
                  <span
                    className={clsx(
                      "h-1 w-1 rounded-full",
                      passwordsMatch ? "bg-green-600" : "bg-red-500"
                    )}
                  />
                  <span
                    className={clsx(
                      passwordsMatch ? "text-green-600" : "text-red-500",
                      "text-xs"
                    )}
                  >
                    {passwordsMatch
                      ? "Passwords match"
                      : "Passwords don't match"}
                  </span>
                </div>
              )}
            </div>
          </Card>

          <div className="mt-6">
            <Button
              text={loading ? <Loader type="bubbles" /> : "Change Password"}
              className={clsx(
                loading ? "!py-1" : "!py-4",
                "!mt-7 !rounded-[50px]"
              )}
              disabled={!isFormValid}
              onClick={handleChangePassword}
            />
          </div>
        </motion.div>
      </div>
    </AppLayout>
  );
}

export default ChangePassword;
